import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab } from '@mui/material';
import { UserCRUD, UserOccupationCRUD, UserTypeCRUD } from '../../components/GeneratedComponents';
import BarChart from '../../components/BarChart';
import GenericIcons from '../../components/generics/GenericIcons';
import { formatStringNumber, spanishMonthNames } from '../../utils/utils';
import { getUncatchEndpointData } from '../../utils/request';
import { EstadoSimulacion } from '../../types';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import 'moment/locale/es';
import '../../css/PrivateAdminPage.css';
import { RefreshMapElements } from '../../components/SecondaryButton';

const PrivateAdminPage: FunctionComponent = () => {
  const [view, setView] = useState<string>('1');
  const [tab, setTab] = useState<string>('1');
  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return view === '1' ? (
    <AdminPage setView={setView} />
  ) : view === '2' ? (
    <div className='private-admin-page-container'>
      <div className='vdr-admin-toolbar'>
        <button className='secondary-button-style vdr-admin-back-btn' onClick={() => setView('1')}>
          <span aria-hidden='true'>←</span>
          Volver
        </button>
      </div>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'var(--vdr-line-strong)' }}>
          <TabList
            onChange={handleChange}
            textColor='inherit'
            TabIndicatorProps={{
              style: {
                backgroundColor: 'var(--vdr-cyan)',
                height: '3px'
              }
            }}>
            <Tab label='Administración de Usuarios' value='1' style={TAB_STYLE} />
            <Tab label='Tipos de Usuario' value='2' style={TAB_STYLE} />
            <Tab label='Ocupaciones de Usuario' value='3' style={TAB_STYLE} />
          </TabList>
        </Box>
        <TabPanel value='1' style={{ padding: 0 }}>
          <UserCRUD />
        </TabPanel>
        <TabPanel value='2' style={{ padding: 0 }}>
          <UserTypeCRUD />
        </TabPanel>
        <TabPanel value='3' style={{ padding: 0 }}>
          <UserOccupationCRUD />
        </TabPanel>
      </TabContext>
    </div>
  ) : (
    //  view === '3'
    <div className='private-admin-page-container'>
      <div className='vdr-admin-toolbar'>
        <button className='secondary-button-style vdr-admin-back-btn' onClick={() => setView('1')}>
          <span aria-hidden='true'>←</span>
          Volver
        </button>
      </div>

      <UserCRUD />
    </div>
  );
};

export default PrivateAdminPage;

const TAB_STYLE = { fontSize: 'clamp(14px, 1.05vw, 16px)', minHeight: 52 };

const AdminPage: FunctionComponent<{ setView: Dispatch<SetStateAction<string>> }> = ({
  setView
}) => {
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(moment(new Date()));
  const [simulationQuantity, setSimulationQuantity] = useState<number>(0);
  const [simulationQuantityInProgress, setSimulationQuantityInProgress] = useState<number>(0);
  const [userOccupationQuantities, setUserOccupationQuantities] = useState<
    Array<{
      userOccupationName: string;
      qty: number;
    }>
  >([]);
  const [userQuantities, setUserQuantities] = useState<
    Array<{
      month: string;
      qty: number;
    }>
  >(
    spanishMonthNames.map((m) => ({
      month: m.slice(0, 3),
      qty: 0
    }))
  );

  useEffect(() => {
    (async () => {
      const res = await getUncatchEndpointData({ endpoint: 'estudios/simulationQuantity' });
      if (res && !isNaN(Number(res))) {
        setSimulationQuantity(res);
      }
    })();

    (async () => {
      const res = await getUncatchEndpointData({
        endpoint: 'estudios/simulationQuantity',
        query: { estadoSimulacion: EstadoSimulacion.inProgress }
      });
      if (res && !isNaN(Number(res))) {
        setSimulationQuantityInProgress(res);
      }
    })();

    (async () => {
      const res = await getUncatchEndpointData({
        endpoint: 'userOccupation/quantity'
      });
      if (res && Array.isArray(res)) {
        setUserOccupationQuantities(
          res.map((row) => ({
            userOccupationName: row.userOccupationName,
            qty: row.qty
          }))
        );
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const res = await getUncatchEndpointData({
        endpoint: 'users/quantity',
        query: { year: selectedDate?.year() }
      });
      if (res && Array.isArray(res)) {
        setUserQuantities(
          spanishMonthNames.map((m, idx) => {
            const findMonth = res.find((row) => row.month === idx + 1);
            if (findMonth) {
              return {
                month: m.slice(0, 3),
                qty: !isNaN(Number(findMonth.qty)) ? findMonth.qty : 0
              };
            }
            return {
              month: m.slice(0, 3),
              qty: 0
            };
          })
        );
      }
    })();
  }, [selectedDate]);

  return (
    <div className='private-admin-page-container'>
      <header className='vdr-page-head vdr-admin-head'>
        <div className='vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>Administrador</span>
          <h2 className='vdr-page-head__title'>Usuarios</h2>
          {/* <RefreshMapElements /> */}
        </div>

        <div className='vdr-page-head__actions'>
          <button className='primary-button-style vdr-admin-manage-btn' onClick={() => setView('2')}>
            Administrar Usuarios
            <span aria-hidden='true'>→</span>
          </button>
        </div>
      </header>
      <div className='private-admin-page-section-container'>
        <div className='private-admin-page-content-container'>
          <h2 className='private-admin-page-content-chart-title'>Tipos de usuarios</h2>
          <div className='private-admin-page-content-chart-container'>
            <BarChart
              dataset={userOccupationQuantities.map((u) => ({
                value: !isNaN(Number(u.qty)) ? Number(u.qty) : 0,
                name: u.userOccupationName || ''
              }))}
            />
          </div>
        </div>
        <div className='private-admin-page-content-container'>
          <div className='private-admin-page-content-chart-title-container'>
            <h2 className='private-admin-page-content-chart-title'>Nuevos usuarios</h2>

            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='Es'>
              <DatePicker
                views={['year']}
                openTo='year'
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
              />
            </LocalizationProvider>
          </div>
          <div className='private-admin-page-content-chart-container'>
            <BarChart
              dataset={userQuantities.map((u) => ({
                value: !isNaN(Number(u.qty)) ? Number(u.qty) : 0,
                name: u.month || ''
              }))}
            />
          </div>
        </div>
      </div>
      <div className='private-admin-page-section-container'>
        <div className='private-admin-page-content-container private-admin-page-kpi-content-container'>
          <h5 className='private-admin-page-total-title'>Total de estudios</h5>
          <h2 className='private-admin-page-total-number'>
            {formatStringNumber((simulationQuantity || '0').toString())}
          </h2>
        </div>
        <div className='private-admin-page-content-container private-admin-page-kpi-content-container'>
          <h5 className='private-admin-page-total-title'>En proceso</h5>
          <h2 className='private-admin-page-total-number private-admin-page-total-number-orange'>
            {formatStringNumber((simulationQuantityInProgress || '0').toString())}
          </h2>
        </div>
        <div className='private-admin-page-content-container private-admin-page-kpi-content-container'>
          <div className='private-admin-page-clipboard-icon-container'>
            <GenericIcons icon='clipboardWhiteIcon' imageStyle={{ height: '100%' }} />
          </div>
          <h5 className='private-admin-page-clipboard-title'>Evaluación Impacto Proyecto</h5>
          <h3 className='private-admin-page-clipboard-number'>#</h3>
        </div>
        <div className='private-admin-page-content-container private-admin-page-kpi-content-container'>
          <div className='private-admin-page-clipboard-icon-container'>
            <GenericIcons icon='clipboardWhiteIcon' imageStyle={{ height: '100%' }} />
          </div>
          <h5 className='private-admin-page-clipboard-title'>Cap. de Aloj. Nivel Nodal</h5>
          <h3 className='private-admin-page-clipboard-number'>#</h3>
        </div>
        <div className='private-admin-page-content-container private-admin-page-kpi-content-container'>
          <div className='private-admin-page-clipboard-icon-container'>
            <GenericIcons icon='clipboardWhiteIcon' imageStyle={{ height: '100%' }} />
          </div>
          <h5 className='private-admin-page-clipboard-title'>Cap. de Aloj. Nivel Alim.</h5>
          <h3 className='private-admin-page-clipboard-number'>#</h3>
        </div>
      </div>
    </div>
  );
};
